import React, { useState, useEffect, useRef } from "react";
import { Loader } from "@mantine/core";
import {
  IconVideo,
  IconRefresh,
  IconUpload,
  IconPlayerRecord,
  IconCheck,
  IconX,
} from "@tabler/icons-react";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../context/ToastContext";
import { getApiEndpoint } from "../../config/config";
import RecordPitchModal from "../Video/RecordPitchModal";
import "./RepPitchReviewPanel.css";

/* ─── helpers ─── */
function timeAgo(dateStr) {
  if (!dateStr) return "";
  const diff = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000);
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${(diff / 3600).toFixed(1)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

const STATUS_CONFIG = {
  approved: { label: "Approved", cls: "rprp-status--approved" },
  needs_work: { label: "Needs Work", cls: "rprp-status--needs-work" },
  resubmit: { label: "Resubmit", cls: "rprp-status--resubmit" },
  pending: { label: "Pending Review", cls: "rprp-status--pending" },
};

const REVIEW_STATUS_TO_BUTTON = {
  approved: "approve",
  needs_work: "needs_work",
  resubmit: "resubmit",
};

/* ════════════════════════════════════════════
   Rep Pitch Review Panel
   ════════════════════════════════════════════ */
export default function RepPitchReviewPanel() {
  const { getVerifiedJWT } = useAuth();
  const { showSuccess } = useToast();

  const [channelId, setChannelId] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null);
  const [filterStatus, setFilterStatus] = useState("all");

  /* upload state */
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showRecordModal, setShowRecordModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadNote, setUploadNote] = useState("");
  const [uploadError, setUploadError] = useState("");
  const [uploading, setUploading] = useState(false);
  const [processingIds, setProcessingIds] = useState(new Set());
  const pollTimers = useRef(new Map());
  const videoRef = useRef(null);

  /* ─── fetch channel + messages ─── */
  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      const jwt = await getVerifiedJWT();
      if (!jwt) {
        setError("Authentication failed");
        return;
      }

      const res = await fetch(
        getApiEndpoint("/v1/pitch-review/my-channel-with-messages"),
        {
          headers: {
            Authorization: `Bearer ${jwt}`,
            "Content-Type": "application/json",
          },
        }
      );
      if (!res.ok) {
        setError("Failed to load your pitch reviews");
        return;
      }

      const data = await res.json();
      const channel = data.data;
      if (!channel) {
        setLoading(false);
        return;
      }

      setChannelId(channel.id);

      const subs = (channel.messages || [])
        .filter(m => !m.is_manager_response && m.response_includes_video)
        .reverse();

      setSubmissions(subs);
      subs.forEach(s => {
        if (!s.response_url) pollForUrl(s.id, jwt);
      });
    } catch {
      setError("Failed to load your pitch reviews");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    const timers = pollTimers.current;
    return () => {
      timers.forEach(t => clearTimeout(t));
      timers.clear();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ─── poll for processed video URL ─── */
  const pollForUrl = async (messageId, jwt, attempt = 0) => {
    if (attempt >= 50) {
      setProcessingIds(prev => {
        const s = new Set(prev);
        s.delete(messageId);
        return s;
      });
      return;
    }
    setProcessingIds(prev => new Set(prev).add(messageId));
    try {
      const res = await fetch(
        getApiEndpoint(`/v1/pitch-review/message/${messageId}`),
        {
          headers: { Authorization: `Bearer ${jwt}` },
        }
      );
      if (res.ok) {
        const msg = (await res.json()).data;
        if (msg.response_url) {
          setSubmissions(prev =>
            prev.map(s =>
              s.id === messageId ? { ...s, response_url: msg.response_url } : s
            )
          );
          setSelected(prev =>
            prev?.id === messageId
              ? { ...prev, response_url: msg.response_url }
              : prev
          );
          setProcessingIds(prev => {
            const s = new Set(prev);
            s.delete(messageId);
            return s;
          });
          return;
        }
      }
    } catch {}
    const tid = setTimeout(() => pollForUrl(messageId, jwt, attempt + 1), 3000);
    pollTimers.current.set(messageId, tid);
  };

  /* ─── upload video ─── */
  const handleUpload = async () => {
    if (!selectedFile || !channelId) {
      setUploadError("Please select a video file.");
      return;
    }
    setUploading(true);
    setUploadError("");
    try {
      const jwt = await getVerifiedJWT();
      if (!jwt) {
        setUploadError("Auth failed");
        return;
      }

      const fileName = `pitch-${Date.now()}.${selectedFile.name.split(".").pop()}`;
      const apiRes = await fetch(
        getApiEndpoint("/v1/pitch-review/video/upload"),
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${jwt}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            channel_id: channelId,
            video_file_name: fileName,
            video_file_content_type: selectedFile.type,
            response_text: uploadNote.trim(),
          }),
        }
      );
      if (!apiRes.ok) throw new Error("Failed to get upload URL");

      const { data } = await apiRes.json();
      const uploadUrl = data.presign?.uploadUrl;
      if (!uploadUrl) throw new Error("No upload URL");

      const putRes = await fetch(uploadUrl, {
        method: "PUT",
        body: selectedFile,
        headers: { "Content-Type": selectedFile.type },
      });
      if (!putRes.ok) throw new Error("File upload failed");

      const newMsg = data.message;
      if (newMsg) {
        setSubmissions(prev => [newMsg, ...prev]);
        setSelected(newMsg);
        const freshJwt = await getVerifiedJWT();
        pollForUrl(newMsg.id, freshJwt);
      }

      showSuccess("Pitch submitted!");
      setShowUploadModal(false);
      setSelectedFile(null);
      setUploadNote("");
    } catch (e) {
      setUploadError(e.message || "Upload failed. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  const handleRecordSubmit = async result => {
    if (result?.success) {
      showSuccess("Pitch submitted!");
      await fetchData();
      setShowRecordModal(false);
    }
  };

  /* ─── filter ─── */
  const filtered = submissions.filter(s => {
    if (filterStatus === "all") return true;
    return (s.approval_status || "pending") === filterStatus;
  });

  const pendingCount = submissions.filter(
    s => !s.approval_status || s.approval_status === "pending"
  ).length;

  /* ════ RENDER ════ */
  return (
    <div className="rprp-panel">
      {/* ══ LEFT: My Submissions List + Submit buttons ══ */}
      <div className="rprp-left">
        <div className="rprp-list-header">
          <div className="rprp-list-title-row">
            <h3 className="rprp-list-title">
              My Pitches
              {pendingCount > 0 && (
                <span className="rprp-pending-badge">
                  {pendingCount} pending
                </span>
              )}
            </h3>
            <button
              className="rprp-refresh-btn"
              onClick={fetchData}
              title="Refresh"
            >
              <IconRefresh size={15} />
            </button>
          </div>

          <div className="rprp-filter-tabs">
            {["all", "pending", "approved", "needs_work", "resubmit"].map(f => (
              <button
                key={f}
                className={`rprp-filter-btn${filterStatus === f ? " active" : ""}`}
                onClick={() => setFilterStatus(f)}
              >
                {f === "all"
                  ? "All"
                  : f === "pending"
                    ? "Pending"
                    : f === "approved"
                      ? "Approved"
                      : f === "needs_work"
                        ? "Needs Work"
                        : "Resubmit"}
              </button>
            ))}
          </div>
        </div>

        {/* Scrollable list */}
        <div className="rprp-list-scroll">
          {loading ? (
            <div className="rprp-loading">
              <Loader size="sm" />
              <span>Loading...</span>
            </div>
          ) : error ? (
            <div className="rprp-list-empty">
              <p style={{ color: "#fa5252" }}>{error}</p>
              <button className="rprp-retry-btn" onClick={fetchData}>
                <IconRefresh size={13} /> Retry
              </button>
            </div>
          ) : filtered.length === 0 ? (
            <div className="rprp-list-empty">
              <IconVideo size={36} />
              <p>
                {submissions.length === 0
                  ? "No pitches submitted yet"
                  : "No pitches match this filter"}
              </p>
            </div>
          ) : (
            filtered.map(sub => {
              const status = sub.approval_status || "pending";
              const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.pending;
              const isSelected = selected?.id === sub.id;
              const isProcessing = processingIds.has(sub.id);
              return (
                <div
                  key={sub.id}
                  className={`rprp-submission-card${isSelected ? " selected" : ""}`}
                  onClick={() => {
                    setSelected(sub);
                    if (videoRef.current) videoRef.current.load();
                  }}
                >
                  <div className="rprp-card-icon">
                    {isProcessing ? (
                      <Loader size={18} />
                    ) : (
                      <IconVideo size={20} />
                    )}
                  </div>
                  <div className="rprp-card-body">
                    <span className="rprp-card-title">
                      {sub.video_title || "Pitch Video"}
                    </span>
                    <span className="rprp-card-time">
                      {timeAgo(sub.created_at)}
                    </span>
                    <span className={`rprp-status-pill ${cfg.cls}`}>
                      {cfg.label}
                    </span>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* ── Submit buttons pinned to bottom of left column ── */}
        <div className="rprp-left-submit">
          <p className="rprp-section-label">Submit a New Pitch</p>
          <div className="rprp-submit-btns">
            <button
              className="rprp-submit-btn rprp-submit-btn--record"
              onClick={() => setShowRecordModal(true)}
              disabled={!channelId}
            >
              <IconPlayerRecord size={16} /> Record Pitch
            </button>
            <button
              className="rprp-submit-btn"
              onClick={() => {
                setShowUploadModal(true);
                setUploadError("");
                setSelectedFile(null);
                setUploadNote("");
              }}
              disabled={!channelId}
            >
              <IconUpload size={16} /> Upload Video
            </button>
          </div>
        </div>
      </div>

      {/* ══ MIDDLE: Selected Pitch Detail ══ */}
      <div className="rprp-middle">
        {!selected ? (
          <div className="rprp-empty-state">
            <IconVideo size={52} />
            <h3>Select a Pitch</h3>
            <p>
              Pick one of your submissions to see its video and manager feedback
            </p>
          </div>
        ) : (
          <>
            <div className="rprp-detail-header">
              <h2 className="rprp-detail-title">
                {selected.video_title || "Pitch Video"}
              </h2>
              <p className="rprp-detail-meta">
                Submitted {timeAgo(selected.created_at)}
              </p>
            </div>

            <div className="rprp-video-section">
              {processingIds.has(selected.id) ? (
                <div className="rprp-video-processing">
                  <Loader size="lg" />
                  <p>Processing video… this may take a minute.</p>
                </div>
              ) : selected.response_url ? (
                <video
                  ref={videoRef}
                  className="rprp-video-player"
                  controls
                  controlsList="nodownload"
                  key={selected.response_url}
                >
                  <source src={selected.response_url} type="video/mp4" />
                </video>
              ) : (
                <div className="rprp-video-placeholder">
                  <IconVideo size={32} />
                  <span>Video is still processing or unavailable</span>
                </div>
              )}
            </div>

            <div className="rprp-note-section">
              <p className="rprp-section-label">My Notes</p>
              <p className="rprp-note-text">
                {selected.response_text?.trim() ||
                  "No notes were sent with this pitch."}
              </p>
            </div>
          </>
        )}
      </div>

      {/* ══ RIGHT: Manager Feedback ══ */}
      <div className="rprp-right">
        {selected ? (
          <div
            className={`rprp-review-form${
              !selected.approval_status ||
              selected.approval_status === "pending"
                ? " rprp-review-form--disabled"
                : ""
            } rprp-review-form--${selected.approval_status || "pending"}`}
          >
            <div className="rprp-review-section rprp-review-section--rating">
              <p className="rprp-section-label">Quick Rating</p>
              <div className="rprp-quick-rating-btns">
                <button
                  className={`rprp-rating-btn approve${
                    REVIEW_STATUS_TO_BUTTON[selected.approval_status] ===
                    "approve"
                      ? " active"
                      : ""
                  }`}
                  disabled
                >
                  <IconCheck size={15} />
                  Approved
                </button>
                <button
                  className={`rprp-rating-btn needs_work${
                    REVIEW_STATUS_TO_BUTTON[selected.approval_status] ===
                    "needs_work"
                      ? " active"
                      : ""
                  }`}
                  disabled
                >
                  <IconX size={15} />
                  Needs Work
                </button>
                <button
                  className={`rprp-rating-btn resubmit${
                    REVIEW_STATUS_TO_BUTTON[selected.approval_status] ===
                    "resubmit"
                      ? " active"
                      : ""
                  }`}
                  disabled
                >
                  <IconX size={15} />
                  Resubmit
                </button>
              </div>
            </div>

            <div className="rprp-review-section" style={{ marginTop: 18 }}>
              <p className="rprp-section-label">Score (out of 5)</p>
              <div className="rprp-score-stars" aria-hidden="true">
                {[1, 2, 3, 4, 5].map(n => {
                  const filled = selected.score >= n;
                  return (
                    <span
                      key={n}
                      className={`rprp-score-star${filled ? " filled" : ""}`}
                    >
                      ★
                    </span>
                  );
                })}
              </div>
              <p className="rprp-score-text">
                {selected.score != null
                  ? `${selected.score}/5`
                  : "No score yet"}
              </p>
            </div>

            <div
              className="rprp-review-section rprp-feedback-section"
              style={{ marginTop: 18 }}
            >
              <p className="rprp-section-label">Manager Feedback</p>
              <textarea
                className="rprp-feedback-textarea"
                readOnly
                value={selected.manager_feedback || ""}
                placeholder="Awaiting feedback from your manager."
              />
              {["resubmit", "needs_work"].includes(
                selected.approval_status
              ) && (
                <div className="rprp-notes-actions">
                  <button
                    className="rprp-resubmit-btn"
                    onClick={() => setShowRecordModal(true)}
                    disabled={!channelId}
                  >
                    <IconPlayerRecord size={16} /> Resubmit Pitch
                  </button>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="rprp-right-empty" />
        )}
      </div>

      {/* ══ Upload Modal ══ */}
      {showUploadModal && (
        <div
          className="rprp-modal-overlay"
          onClick={() => setShowUploadModal(false)}
        >
          <div className="rprp-modal" onClick={e => e.stopPropagation()}>
            <div className="rprp-modal-header">
              <h3>Upload Pitch Video</h3>
              <button
                className="rprp-modal-close"
                onClick={() => setShowUploadModal(false)}
              >
                ×
              </button>
            </div>
            <div className="rprp-modal-body">
              {uploadError && (
                <div className="rprp-upload-error">{uploadError}</div>
              )}
              <label className="rprp-field-label">Video File *</label>
              <input
                type="file"
                accept="video/*"
                onChange={e => setSelectedFile(e.target.files[0] || null)}
                disabled={uploading}
              />
              <label className="rprp-field-label" style={{ marginTop: "1rem" }}>
                Note (optional)
              </label>
              <input
                type="text"
                className="rprp-field-input"
                placeholder="Anything to say about this pitch?"
                value={uploadNote}
                onChange={e => setUploadNote(e.target.value)}
                disabled={uploading}
              />
            </div>
            <div className="rprp-modal-footer">
              <button
                className="rprp-modal-cancel"
                onClick={() => setShowUploadModal(false)}
                disabled={uploading}
              >
                Cancel
              </button>
              <button
                className="rprp-modal-upload"
                onClick={handleUpload}
                disabled={!selectedFile || uploading}
              >
                {uploading ? (
                  <Loader size="xs" color="white" />
                ) : (
                  "Upload & Submit"
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ══ Record Modal ══ */}
      {showRecordModal && (
        <RecordPitchModal
          opened={showRecordModal}
          onClose={() => setShowRecordModal(false)}
          onSubmit={handleRecordSubmit}
          videoTitle="Pitch Review Video"
          videoId={null}
          courseId={null}
          initialChannelId={channelId}
          onSubmitSuccess={() => {}}
        />
      )}
    </div>
  );
}
